import { useCallback, useRef } from 'react';
import type ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import {
  exportChartToPNG,
  exportToCSV,
  exportToXLSX,
  extractChartData,
  ExportFormat,
  type ExportOptions,
  type ChartExportOptions
} from '@/shared/lib/export';

interface UseChartExportProps {
  chartOption: EChartsOption | Record<string, unknown>;
  exportOptions?: {
    png?: ChartExportOptions;
    csv?: ExportOptions;
    xlsx?: ExportOptions;
  };
  /** External chart ref to export from; the hook creates its own when omitted */
  chartRef?: React.RefObject<ReactECharts | null>;
}

interface UseChartExportResult {
  chartRef: React.RefObject<ReactECharts | null>;
  exportChart: (format: ExportFormat) => void;
  exportPNG: () => void;
  exportCSV: () => void;
  exportXLSX: () => void;
}

export function useChartExport({
  chartOption,
  exportOptions,
  chartRef: externalChartRef
}: UseChartExportProps): UseChartExportResult {
  const internalChartRef = useRef<ReactECharts>(null);
  const chartRef = externalChartRef ?? internalChartRef;

  const exportPNG = useCallback(() => {
    const instance = chartRef.current?.getEchartsInstance();
    exportChartToPNG(instance, exportOptions?.png);
  }, [chartRef, exportOptions?.png]);

  const exportCSV = useCallback(() => {
    const data = extractChartData(chartOption);
    exportToCSV(data, exportOptions?.csv);
  }, [chartOption, exportOptions?.csv]);

  const exportXLSX = useCallback(() => {
    const data = extractChartData(chartOption);
    exportToXLSX(data, exportOptions?.xlsx);
  }, [chartOption, exportOptions?.xlsx]);

  const exportChart = useCallback(
    (format: ExportFormat) => {
      switch (format) {
        case ExportFormat.PNG:
          exportPNG();
          break;
        case ExportFormat.CSV:
          exportCSV();
          break;
        case ExportFormat.XLSX:
          exportXLSX();
          break;
        default:
          console.warn(`Unsupported export format: ${format}`);
      }
    },
    [exportPNG, exportCSV, exportXLSX]
  );

  return {
    chartRef,
    exportChart,
    exportPNG,
    exportCSV,
    exportXLSX
  };
}
